import axios from 'axios'
import { getBaseAPI } from '@/environments'
import cookie from '@/utils/cookie'
import AmplitudeService from '@/services/amplitude'
const kulapApi = axios.create({
  baseURL: `${getBaseAPI()}`,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 45000
})

kulapApi.interceptors.response.use((response) => response, (error) => {
  AmplitudeService.axiosResponseError(error)
  return Promise.reject(error)
})

const CancelToken = axios.CancelToken
export default class KulapAPIProvider {
  constructor () {
    this.getBestRateCanceller = null
  }

  cancelBestRateRequest () {
    if (this.getBestRateCanceller) {
      this.getBestRateCanceller('REQUEST_CANCELD')
    }
  }

  getBestRateFromFromAmount (from, to, fromAmount) {
    return kulapApi.get('/rate/best-rate/toAmount', {
      params: { from, to, fromAmount },
      cancelToken: new CancelToken((cancel) => {
        this.getBestRateCanceller = cancel
      })
    })
  }

  getBestRateFromToAmount (from, to, toAmount) {
    return kulapApi.get('/rate/best-rate/fromAmount', {
      params: { from, to, toAmount },
      cancelToken: new CancelToken((cancel) => {
        this.getBestRateCanceller = cancel
      })
    })
  }

  getAssetsProfile () {
    return kulapApi.get('/assets/profile')
  }

  getAssetsAvaliable () {
    return kulapApi.get('/assets/listed')
  }

  postRegisterNewUser (data) {
    const language = localStorage.getItem('KULAP.user-lang') || 'en'
    const mapData = { ...data, language }
    return kulapApi.post('/user/registration', mapData)
  }

  postLoginWithFacebook (data) {
    const language = localStorage.getItem('KULAP.user-lang') || 'en'
    const mapData = { ...data, language }
    return kulapApi.post('/user/loginWithFacebook', mapData)
  }

  postLoginWithGoogle (data) {
    const language = localStorage.getItem('KULAP.user-lang') || 'en'
    const mapData = { ...data, language }
    return kulapApi.post('/user/loginWithGoogle', mapData)
  }

  postLoginWithEmailPassword (email, password, recaptchaToken) {
    return kulapApi.post('/user/loginWithEmailPassword', { email, password, recaptchaToken })
  }

  getUserProfile () {
    return kulapApi.get('/user/profile', {
      headers: {
        'Authorization': cookie.get('accessToken')
      }
    })
  }

  updateUserProfile (userData) {
    return kulapApi.put('/user/profile', userData, {
      headers: {
        'Authorization': cookie.get('accessToken')
      }
    })
  }

  updateProfilePicture (formData) {
    return kulapApi.put('/user/profile/avatar', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': cookie.get('accessToken')
      }
    })
  }

  putRewardsOption (rewards) {
    return kulapApi.put('/user/reward/options', rewards, {
      headers: {
        'Authorization': cookie.get('accessToken')
      }
    })
  }

  sendConfirmationEmail (recaptchaToken) {
    const language = localStorage.getItem('KULAP.user-lang') || 'en'
    const mapData = { language, recaptchaToken }
    return kulapApi.post('/user/send-confirmation-email', mapData, {
      headers: {
        'Authorization': cookie.get('accessToken')
      }
    })
  }

  sendForgotPasswordEmail (data) {
    const language = localStorage.getItem('KULAP.user-lang') || 'en'
    const mapData = { ...data, language }
    return kulapApi.post('/user/send-forgot-password-email', mapData, {
      headers: {
        'Authorization': cookie.get('accessToken')
      }
    })
  }

  confirmEmail (data) {
    return kulapApi.put('/user/activate', data, {
      headers: {
        'Authorization': cookie.get('accessToken')
      }
    })
  }

  updatePassword (email, password, resetPasswordToken, recaptchaToken) {
    return kulapApi.put('/user/reset-password', { email, password, resetPasswordToken, recaptchaToken }, {
      headers: {
        'Authorization': cookie.get('accessToken')
      }
    })
  }

  patchSubmitKycForm (form) {
    const language = localStorage.getItem('KULAP.user-lang') || 'en'
    const mapData = { ...form, language }
    return kulapApi.patch('/user/submit-kyc', mapData, {
      headers: {
        'Authorization': cookie.get('accessToken')
      }
    })
  }

  unlinkSocial (social) {
    return kulapApi.put(`/user/unlink/${social}`, {}, {
      headers: {
        'Authorization': cookie.get('accessToken')
      }
    })
  }

  getKycFormData () {
    return kulapApi.get('/user/kyc', {
      headers: {
        'Authorization': cookie.get('accessToken')
      }
    })
  }

  getGenerate2FA () {
    return kulapApi.get('/user/2fa/generate', {
      headers: {
        'Authorization': cookie.get('accessToken')
      }
    })
  }

  putEnable2FA (code) {
    return kulapApi.put('/user/2fa/enable', { code }, {
      headers: {
        'Authorization': cookie.get('accessToken')
      }
    })
  }

  putDisable2FA (code) {
    return kulapApi.put('/user/2fa/disable', { code }, {
      headers: {
        'Authorization': cookie.get('accessToken')
      }
    })
  }

  getVerify2FA (code, secretKeyForVerify2FA, userId) {
    return kulapApi.get(`/user/2fa/verify`, {
      params: {
        code,
        secretKeyForVerify2FA,
        userId
      }
    })
  }

  putUpdateUserNotificationAsRead (timestamp) {
    return kulapApi.put('/user/notification/mark-as-read', { timestamp }, {
      headers: {
        'Authorization': cookie.get('accessToken')
      }
    })
  }

  getUserReferrals () {
    return kulapApi.get('/user/referrals', {
      headers: {
        'Authorization': cookie.get('accessToken')
      }
    })
  }

  getKycVerification () {
    return kulapApi.get('/user/kyc/verification', {
      headers: {
        'Authorization': cookie.get('accessToken')
      }
    })
  }

  updateUserEmail (data) {
    const language = localStorage.getItem('KULAP.user-lang') || 'en'
    const mapData = { ...data, language }
    return kulapApi.put('/user/update-user-email', mapData, {
      headers: {
        'Authorization': cookie.get('accessToken')
      }
    })
  }

  postUploadKycDocs (formData) {
    return kulapApi.post('/user/upload/kyc/docs', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': cookie.get('accessToken')
      }
    })
  }

  patchSubmitSuitTest (data) {
    return kulapApi.patch('/user/suit-test/submit', data, {
      headers: {
        'Authorization': cookie.get('accessToken')
      }
    })
  }

  patchSubmitKnowledgeTest (data) {
    return kulapApi.patch('/user/knowledge-test/submit', data, {
      headers: {
        'Authorization': cookie.get('accessToken')
      }
    })
  }

  putUserEthAddress (data) {
    return kulapApi.put('/user/address', data, {
      headers: {
        'Authorization': cookie.get('accessToken')
      }
    })
  }

  postTradingObjective (objective, fromToken, fromTokenAmount) {
    return kulapApi.post('/user/trading/objective', { objective, fromToken, fromTokenAmount }, {
      headers: {
        'Authorization': cookie.get('accessToken')
      }
    })
  }
  postLogout () {
    return kulapApi.post('/user/logout', null, {
      headers: {
        'Authorization': cookie.get('accessToken')
      }
    })
  }
  getTopTradeEvent (myUserId) {
    return kulapApi.get('top-trader-event/stats', {
      params: { userId: myUserId }
    })
  }
  getTradeHistories (params) {
    return kulapApi.get('/trade-history', {
      params,
      headers: {
        'Authorization': cookie.get('accessToken')
      }
    })
  }

  getTradeHistoriesFile (params) {
    return kulapApi.get('/trade-history/report', {
      params,
      headers: {
        'Authorization': cookie.get('accessToken')
      }
    })
  }

  postCookieConsent (data) {
    return kulapApi.post('/cookie-consent', data)
  }

  getUserKnowledgeTest (params) {
    return kulapApi.get('/user/knowledge-test', {
      params,
      headers: {
        'Authorization': cookie.get('accessToken')
      }
    })
  }
}



// WEBPACK FOOTER //
// ./src/resources/kulapApi.js